<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $t('messages.dashboard'),
                        href: meta.links.dashboard_index,
                    },
                    {
                        name: $tc('messages.relatives', 2),
                        href: meta.links.index,
                    },
                    {
                        name: relative.full_name,
                        href: null,
                    },
                ]"
            />
        </template>

        <div class="w-full flex space-x-4 justify-end">
            <inertia-link-button type="white" :href="meta.links.edit">
                {{ $t('messages.edit') }}
            </inertia-link-button>
        </div>

        <div>
            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.name') }}
                    <div class="text-gray-400">
                        {{ relative.full_name }}
                    </div>
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.first_name')" for="first_name" />
                            {{ relative.first_name }}
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.nickname')" for="name" />
                            {{ relative.name }}
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.last_name')" for="last_name" />
                            {{ relative.last_name }}
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.second_last_name')" for="second_last_name" />
                            {{ relative.second_last_name }}
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.information') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.date_of_birth')" for="date_of_birth" />
                            {{ relative.date_of_birth }}
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.gender')" for="gender" />
                            {{ relative.gender }}
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.mother_tongue')" for="mother_tongue" />
                            {{ relative.mother_tongue }}
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.nationality')" for="nationality" />
                            {{ relative.nationality }}
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.country_of_origin')" for="country_of_origin" />
                            {{ relative.country_of_origin }}
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.settings') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.preferred_language')" for="preferred_language" />
                            {{ relative.preferred_language }}
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.contact_info') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.email')" for="email" />
                            {{ relative.email }}
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $tc('messages.phones', 2) }}
                </template>
                <PhonesList :phones="relative.phones" />
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $tc('messages.students', 2) }}
                </template>
                <div class="md:grid md:grid-cols-2 md:gap-6">
                    <div
                        class="relative col-span-1 flex shadow rounded-md"
                        v-for="student in relative.students"
                        :key="student.id"
                    >
                        <div
                            class="shrink-0 flex items-center justify-center w-16 text-white text-sm leading-5 font-medium rounded-l-md overflow-hidden"
                        >
                            <a :href="student.links.show">
                                <img :src="student.avatar_or_default" alt="profile photo" />
                            </a>
                        </div>
                        <div
                            class="flex-1 flex items-center justify-between border-t border-r border-b border-gray-200 bg-white rounded-r-md truncate"
                        >
                            <div class="flex-1 px-4 py-2 text-sm leading-5 truncate">
                                <a
                                    :href="student.links.show"
                                    class="text-gray-900 font-medium hover:text-gray-600 transition ease-in-out duration-150"
                                >
                                    <div>
                                        <div>
                                            {{ student.full_name }}
                                        </div>
                                        <div>
                                            <BadgePill color="yellow" v-if="!student.active">
                                                {{ $t('messages.inactive') }}
                                            </BadgePill>
                                        </div>
                                    </div>
                                    <CanTakeStudent :value="student.can_take_student"></CanTakeStudent>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>
        </div>
    </PanelPage>
</template>

<script>
import AdminLayout from '@/Shared/AdminLayout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import CanTakeStudent from '@/components/CanTakeStudent.vue';
import PhonesList from '@/Pages/Admin/Relatives/PhonesList.vue';
import BadgePill from '@/components/BadgePill.vue';

export default {
    components: {
        PhonesList,
        PanelPage,
        TitleFieldsFormSection,
        FormLabel,
        CanTakeStudent,
        BadgePill,
    },
    layout: AdminLayout,
    name: 'Show',
    props: ['relative', 'meta'],
    data() {
        return {};
    },
    methods: {},
    computed: {},
};
</script>
